import type jsPDF from 'jspdf';
import { fetchCallsInRange, fetchCallStatsByType, fetchExtensions } from '@/services/api';
import type { CallRecord, CallStats, Extension } from '@/types';
import { mergeDirectionStats, type ExtensionActivity } from './extensionActivity';
import { dispositionBucket } from '@/utils/callRecords';
import { filterStatCalls } from '@/components/dashboard/statCalls';
import { phonesLikelyMatch } from '@/lib/failoverCorrelation';
import { callCost, callingExtension, dialledNumber, formatRand, type CallRates, type CostTotals } from '@/lib/callCosts';
import { BrandPdf, PDF_COLORS, type PdfValue } from '@/lib/pdf/brandPdf';
import type { PdfAssets } from '@/lib/pdf/pdfAssets';
import { FORWARDED, formatDay, formatDurationHMS, formatGenerated, partyText } from './callCostReport';
import { csvText } from './extensionReports';

/**
 * Daily Activity: the day (or week, month) for every extension. The totals and the per-extension
 * counts are the dashboard's Today's Stats (Yeastar's call statistics); the missed calls, and the
 * calls made with their cost, come from the call records.
 */

export type FollowUp =
  | { kind: 'calledBack'; atMs: number; by: string; answered: boolean }
  | { kind: 'rangAgain'; atMs: number; by: string }
  | { kind: 'none' }
  | { kind: 'unknownCaller' };

export interface MissedCall {
  record: CallRecord;
  atMs: number;
  caller: string;
  /** The extension or queue that missed it. */
  missedBy: string;
  queue: boolean;
  followUp: FollowUp;
}

export interface MadeCalls {
  /** Extension number, or FORWARDED for calls with no extension behind them. */
  extension: string;
  label: string;
  calls: number;
  answered: number;
  talkSeconds: number;
  mobile: CostTotals;
  landline: CostTotals;
  cost: number;
  records: CallRecord[];
}

export interface DailyActivity {
  startDate: string;
  endDate: string;
  rates: CallRates;
  /** Extensions with at least one call, by number. */
  activity: ExtensionActivity[];
  /** Extensions on the PBX with no calls at all. */
  quietExtensions: number;
  totals: {
    received: number;
    made: number;
    total: number;
    missed: number;
    missedAtExtensions: number;
    missedInQueues: number;
    talkSeconds: number;
  };
  missedCalls: MissedCall[];
  made: MadeCalls[];
  /** Totals of the calls made, from the call records. */
  madeTotals: { calls: number; answered: number; talkSeconds: number; mobile: number; landline: number; cost: number };
}

export function recordTimeMs(record: CallRecord) {
  if (record.timestamp) return record.timestamp * 1000;
  const parsed = Date.parse(String(record.time || '').replace(/-/g, '/'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** The caller's own number from an inbound record ("Name<0821234567>" → "0821234567"). */
export function callerNumber(record: CallRecord) {
  const raw = String(record.call_from_number || record.call_from || '').trim();
  const bracketed = raw.match(/<([^>]+)>\s*$/);
  return (bracketed ? bracketed[1] : raw).replace(/[^\d+]/g, '');
}

/** "Sales (6400)" from "Queue Sales<6400>". */
export function missedByLabel(record: CallRecord) {
  const raw = String(record.call_to || '').replace(/^Queue\s+/i, '');
  return partyText(raw);
}

/** The number in "Marco Hart<103>" or "103". */
function partyNumber(value?: string) {
  const raw = String(value || '').trim();
  const bracketed = raw.match(/<([^>]+)>\s*$/);
  return (bracketed ? bracketed[1] : raw).trim();
}

/**
 * What happened after a missed call, within the report's calls: a call back, or the caller ringing
 * again and getting through to a person (an extension, not an IVR or a queue).
 */
export function followUpOf(
  missed: CallRecord,
  atMs: number,
  records: CallRecord[],
  extensionLabel: (n: string) => string,
  extensionNumbers: Set<string>
): FollowUp {
  const number = callerNumber(missed);
  if (number.replace(/\D/g, '').length < 6) return { kind: 'unknownCaller' };
  let best: FollowUp = { kind: 'none' };
  const earlier = (time: number) => best.kind === 'none' || best.kind === 'unknownCaller' || time < best.atMs;
  for (const record of records) {
    const time = recordTimeMs(record);
    if (time <= atMs || !earlier(time)) continue;
    if (record.call_type === 'Outbound' && phonesLikelyMatch(dialledNumber(record), number)) {
      const from = callingExtension(record);
      best = {
        kind: 'calledBack',
        atMs: time,
        by: from ? extensionLabel(from) : partyText(record.call_from),
        answered: dispositionBucket(record) === 'answered',
      };
    } else if (
      record.call_type === 'Inbound' &&
      dispositionBucket(record) === 'answered' &&
      extensionNumbers.has(partyNumber(record.call_to)) &&
      phonesLikelyMatch(callerNumber(record), number)
    ) {
      best = { kind: 'rangAgain', atMs: time, by: missedByLabel(record) };
    }
  }
  return best;
}

const emptyTotals = (): CostTotals => ({ calls: 0, seconds: 0, cost: 0 });
const round2 = (value: number) => Math.round(value * 100) / 100;

/** The report from the PBX's statistics and call records (no fetching, for tests and both callers). */
export function buildDailyActivity(input: {
  startDate: string;
  endDate: string;
  rates: CallRates;
  extensions: Extension[];
  inbound: CallStats[];
  outbound: CallStats[];
  records: CallRecord[];
}): DailyActivity {
  const names = new Map(input.extensions.map((ext) => [ext.number, ext.display_name || ext.username || '']));
  const extensionLabel = (number: string) =>
    number === FORWARDED ? 'Forwarded / other' : `${number}${names.get(number) ? ` - ${names.get(number)}` : ''}`;

  const merged = mergeDirectionStats(input.inbound, input.outbound);
  const activity = merged.filter((row) => row.received + row.made > 0);
  const extensionNumbers = input.extensions.map((ext) => String(ext.number));

  const missedRecords = filterStatCalls(input.records, 'missed', extensionNumbers);
  const missedCalls: MissedCall[] = missedRecords
    .map((record) => {
      const atMs = recordTimeMs(record);
      return {
        record,
        atMs,
        caller: partyText(record.call_from),
        missedBy: missedByLabel(record),
        queue: /^Queue\s/i.test(String(record.call_to || '')),
        followUp: followUpOf(record, atMs, input.records, extensionLabel, new Set(extensionNumbers)),
      };
    })
    .sort((a, b) => a.atMs - b.atMs);
  const missedInQueues = missedCalls.filter((call) => call.queue).length;

  const made = new Map<string, MadeCalls>();
  for (const record of input.records) {
    if (record.call_type !== 'Outbound') continue;
    const extension = callingExtension(record) ?? FORWARDED;
    const row =
      made.get(extension) ??
      ({
        extension,
        label: extensionLabel(extension),
        calls: 0,
        answered: 0,
        talkSeconds: 0,
        mobile: emptyTotals(),
        landline: emptyTotals(),
        cost: 0,
        records: [],
      } satisfies MadeCalls);
    const cost = callCost(record, input.rates);
    row.calls += 1;
    if (dispositionBucket(record) === 'answered') row.answered += 1;
    row.talkSeconds += Math.max(0, Math.round(record.talk_duration || 0));
    if (cost.category === 'mobile' || cost.category === 'landline') {
      const totals = row[cost.category];
      totals.calls += cost.billableSeconds > 0 ? 1 : 0;
      totals.seconds += cost.billableSeconds;
      totals.cost = round2(totals.cost + cost.cost);
    }
    row.cost = round2(row.cost + cost.cost);
    row.records.push(record);
    made.set(extension, row);
  }
  const madeRows = Array.from(made.values()).sort((a, b) =>
    a.extension === FORWARDED ? 1 : b.extension === FORWARDED ? -1 : a.extension.localeCompare(b.extension, undefined, { numeric: true })
  );
  for (const row of madeRows) row.records.sort((a, b) => recordTimeMs(a) - recordTimeMs(b));

  const sum = <T>(rows: T[], pick: (row: T) => number) => rows.reduce((total, row) => total + pick(row), 0);
  const missedAtExtensions = sum(merged, (row) => row.missed);
  const received = sum(merged, (row) => row.received);
  const madeCount = sum(merged, (row) => row.made);
  return {
    startDate: input.startDate,
    endDate: input.endDate,
    rates: input.rates,
    activity,
    quietExtensions: Math.max(0, input.extensions.length - activity.length),
    totals: {
      received,
      made: madeCount,
      total: received + madeCount,
      missed: missedAtExtensions + missedInQueues,
      missedAtExtensions,
      missedInQueues,
      talkSeconds: sum(merged, (row) => row.duration),
    },
    missedCalls,
    made: madeRows,
    madeTotals: {
      calls: sum(madeRows, (row) => row.calls),
      answered: sum(madeRows, (row) => row.answered),
      talkSeconds: sum(madeRows, (row) => row.talkSeconds),
      mobile: round2(sum(madeRows, (row) => row.mobile.cost)),
      landline: round2(sum(madeRows, (row) => row.landline.cost)),
      cost: round2(sum(madeRows, (row) => row.cost)),
    },
  };
}

/** Fetches the statistics and call records for the dates and builds the report. */
export async function loadDailyActivity(startDate: string, endDate: string, rates: CallRates): Promise<DailyActivity> {
  const start = `${startDate.replace(/-/g, '/')} 00:00:00`;
  const end = `${endDate.replace(/-/g, '/')} 23:59:59`;
  const extensions = await fetchExtensions(false);
  const ids = extensions.map((ext) => ext.id);
  const [inbound, outbound, records] = await Promise.all([
    ids.length ? fetchCallStatsByType(ids, start, end, 'Inbound') : Promise.resolve([]),
    ids.length ? fetchCallStatsByType(ids, start, end, 'Outbound') : Promise.resolve([]),
    fetchCallsInRange(start, end),
  ]);
  return buildDailyActivity({ startDate, endDate, rates, extensions, inbound, outbound, records });
}

/** "08:14" for a one-day report, "Mon 13 Oct, 08:14" otherwise. */
export function activityTimeFormatter(startDate: string, endDate: string) {
  const oneDay = startDate === endDate;
  return (ms: number) => {
    if (!ms) return '-';
    const date = new Date(ms);
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    if (oneDay) return time;
    const day = date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return `${day}, ${time}`;
  };
}

export function describeFollowUp(followUp: FollowUp, formatTime: (ms: number) => string): string {
  switch (followUp.kind) {
    case 'calledBack':
      return `Called back ${formatTime(followUp.atMs)} by ${followUp.by}${followUp.answered ? '' : ' (no answer)'}`;
    case 'rangAgain':
      return `Rang again ${formatTime(followUp.atMs)}, answered (${followUp.by})`;
    case 'unknownCaller':
      return 'No number to call back';
    default:
      return 'Not called back';
  }
}

export function missedStatus(record: CallRecord, queue: boolean) {
  const bucket = dispositionBucket(record);
  if (queue) return 'Queue: no answer';
  if (bucket === 'busy') return 'Busy';
  if (bucket === 'failed') return 'Failed';
  return 'No answer';
}

const c = PDF_COLORS;

const minutes = (seconds: number) => `${Math.round(seconds / 6) / 10} min`;

/** "20s", "1m 05s" */
export function ringTime(seconds: number) {
  const s = Math.max(0, Math.round(seconds));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

export function dailyActivityPdf(
  data: DailyActivity,
  options: { customerName?: string; assets?: PdfAssets; now?: Date; includeCallLists: boolean }
): jsPDF {
  const { totals } = data;
  const formatTime = activityTimeFormatter(data.startDate, data.endDate);
  const oneDay = data.startDate === data.endDate;
  const doc = new BrandPdf({
    orientation: 'portrait',
    assets: options.assets,
    reportName: 'Daily activity',
    title: oneDay
      ? new Date(`${data.startDate}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : `${formatDay(data.startDate)} – ${formatDay(data.endDate)}`,
    subtitle: 'All extensions',
    customerName: options.customerName,
    generated: `Generated ${formatGenerated(options.now ?? new Date())}`,
  });

  doc.statTiles([
    { label: 'Total calls', value: String(totals.total), caption: 'Received + made', featured: true },
    { label: 'Received', value: String(totals.received), dot: c.accent, caption: 'Inbound calls' },
    { label: 'Made', value: String(totals.made), dot: c.indigo, caption: 'Outbound' },
    {
      label: 'Missed',
      value: String(totals.missed),
      dot: c.red,
      caption: `${totals.missedInQueues} in queues, ${totals.missedAtExtensions} at ext.`,
    },
    { label: 'Talk time', value: formatDurationHMS(totals.talkSeconds), caption: 'Time on calls' },
  ]);

  const notReturned = data.missedCalls.filter((call) => call.followUp.kind === 'none').length;
  doc.table({
    title: 'Missed calls',
    meta: data.missedCalls.length
      ? `${data.missedCalls.length} call${data.missedCalls.length === 1 ? '' : 's'} · ${notReturned} not called back`
      : 'None',
    columns: [
      { header: oneDay ? 'Time' : 'When', width: oneDay ? 14 : 28 },
      { header: 'Caller', width: 46, wrap: true },
      { header: 'Missed by', width: 30, wrap: true },
      { header: 'Result', width: 30 },
      { header: 'Ring', width: 13, align: 'right' },
      { header: 'Follow-up', width: 58, wrap: true },
    ],
    rows: data.missedCalls.length
      ? data.missedCalls.map((call) => [
          formatTime(call.atMs),
          call.caller,
          call.missedBy,
          { text: missedStatus(call.record, call.queue), color: call.queue ? c.orange : c.red, badge: true },
          ringTime(call.record.ring_duration || 0),
          call.followUp.kind === 'none'
            ? { text: describeFollowUp(call.followUp, formatTime), color: c.red, bold: true }
            : call.followUp.kind === 'unknownCaller'
              ? { text: describeFollowUp(call.followUp, formatTime), color: c.muted }
              : { text: describeFollowUp(call.followUp, formatTime), color: c.green },
        ])
      : [['', 'No missed calls', '', '', '', '']],
  });

  const activityTotal: PdfValue[] = [
    'Total',
    String(totals.received),
    { text: String(totals.missedAtExtensions), color: totals.missedAtExtensions ? c.red : c.ink },
    String(totals.made),
    String(totals.total),
    { text: formatDurationHMS(totals.talkSeconds), color: c.accentDark },
  ];
  doc.table({
    title: 'By extension',
    meta: `${data.activity.length} extension${data.activity.length === 1 ? '' : 's'} with calls${
      data.quietExtensions ? ` · ${data.quietExtensions} with none` : ''
    }`,
    columns: [
      { header: 'Extension', width: 56 },
      { header: 'Received', width: 18, align: 'right' },
      { header: 'Missed', width: 16, align: 'right' },
      { header: 'Made', width: 16, align: 'right' },
      { header: 'Total', width: 16, align: 'right' },
      { header: 'Talk time', width: 22, align: 'right' },
    ],
    rows: data.activity.map((row) => [
      `${row.ext_num}${row.ext_name && row.ext_name !== row.ext_num ? ` - ${row.ext_name}` : ''}`,
      String(row.received),
      row.missed ? { text: String(row.missed), color: c.red } : '0',
      String(row.made),
      { text: String(row.received + row.made), bold: true, color: c.ink },
      formatDurationHMS(row.duration),
    ]),
    total: activityTotal,
  });

  const m = data.madeTotals;
  doc.table({
    title: 'Calls made',
    meta: `Mobile ${formatRand(data.rates.mobilePerMinute)}/min · landline ${formatRand(data.rates.landlinePerMinute)}/min · per second`,
    columns: [
      { header: 'Extension', width: 48 },
      { header: 'Calls', width: 13, align: 'right' },
      { header: 'Answered', width: 17, align: 'right' },
      { header: 'Minutes', width: 18, align: 'right' },
      { header: 'Mobile', width: 18, align: 'right' },
      { header: 'Landline', width: 18, align: 'right' },
      { header: 'Cost', width: 18, align: 'right' },
    ],
    rows: data.made.length
      ? data.made.map((row) => [
          row.label,
          String(row.calls),
          String(row.answered),
          minutes(row.talkSeconds),
          formatRand(row.mobile.cost),
          formatRand(row.landline.cost),
          { text: formatRand(row.cost), bold: true, color: c.ink },
        ])
      : [['No calls made', '', '', '', '', '', '']],
    total: data.made.length
      ? ['Total', String(m.calls), String(m.answered), minutes(m.talkSeconds), formatRand(m.mobile), formatRand(m.landline), { text: formatRand(m.cost), color: c.accentDark }]
      : undefined,
  });

  if (options.includeCallLists) {
    for (const row of data.made) {
      doc.table({
        title: row.label,
        meta: `${row.calls} call${row.calls === 1 ? '' : 's'} · ${minutes(row.talkSeconds)} · ${formatRand(row.cost)}`,
        columns: [
          { header: oneDay ? 'Time' : 'When', width: oneDay ? 14 : 28 },
          { header: 'Number called', width: 52 },
          { header: 'Result', width: 24 },
          { header: 'Talk time', width: 20, align: 'right' },
          { header: 'Cost', width: 18, align: 'right' },
        ],
        rows: row.records.map((record) => {
          const cost = callCost(record, data.rates);
          const bucket = dispositionBucket(record);
          return [
            formatTime(recordTimeMs(record)),
            partyText(record.call_to),
            {
              text: record.disposition || 'Unknown',
              color: bucket === 'answered' ? c.green : bucket === 'failed' ? c.muted : c.orange,
              badge: true,
            },
            formatDurationHMS(Math.max(0, Math.round(record.talk_duration || 0))),
            cost.category ? formatRand(cost.cost) : '-',
          ];
        }),
      });
    }
  }
  doc.note(
    'Totals and the by-extension counts are Yeastar’s call statistics, as on the dashboard; missed calls also count call queue calls nobody answered. ' +
      'Missed calls and calls made come from the call records. A missed call counts as called back when someone phoned that number later in the report period. ' +
      'Costs are for answered outbound calls, billed per second; toll-free (080) calls are free and international numbers are not priced.'
  );
  return doc.finish();
}

export function dailyActivityCsvs(data: DailyActivity) {
  const formatTime = (ms: number) => {
    if (!ms) return '';
    const date = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };
  const made = new Map(data.made.map((row) => [row.extension, row]));
  const extensions = csvText(
    [
      'Extension',
      'Name',
      'Received',
      'Missed',
      'Made',
      'Total',
      'Talk time',
      'Talk seconds',
      'Calls made (records)',
      'Made answered',
      'Made talk seconds',
      'Mobile cost (R)',
      'Landline cost (R)',
      'Cost (R)',
    ],
    data.activity.map((row) => {
      const out = made.get(row.ext_num);
      return [
        row.ext_num,
        row.ext_name,
        row.received,
        row.missed,
        row.made,
        row.received + row.made,
        formatDurationHMS(row.duration),
        row.duration,
        out?.calls ?? 0,
        out?.answered ?? 0,
        out?.talkSeconds ?? 0,
        (out?.mobile.cost ?? 0).toFixed(2),
        (out?.landline.cost ?? 0).toFixed(2),
        (out?.cost ?? 0).toFixed(2),
      ];
    })
  );
  const missed = csvText(
    ['Time', 'Caller', 'Missed by', 'Result', 'Ring seconds', 'Follow-up'],
    data.missedCalls.map((call) => [
      formatTime(call.atMs),
      call.caller,
      call.missedBy,
      missedStatus(call.record, call.queue),
      Math.round(call.record.ring_duration || 0),
      describeFollowUp(call.followUp, formatTime),
    ])
  );
  return { extensions, missed };
}
